import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import LastSeen from '../LastSeen';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark, faUserPlus, faLink,
    faBell, faBellSlash, faRightFromBracket, faTrash, faPen, faCopy, faCamera,
    faComment, faPhone, faListCheck
} from '@fortawesome/free-solid-svg-icons';

import api from '../../utils/api';
import RichTextEditor from '../RichTextEditor';
import RichText from '../RichText';
import Avatar from '../Avatar';
import ImageEditor from '../ImageEditor';
import PeoplePicker from './PeoplePicker';
import GroupIcon from './GroupIcon';
import ExternalParticipants from './ExternalParticipants';
import SharedContent from './SharedContent';
import { GROUP_DESCRIPTION_LIMIT } from './chatLimits';
import { orgLabel } from '../../utils/orgTaxonomy';
import WithPresence from '../PresenceDot';
import { useActivityAccess } from '../../utils/personActivity';
import { useCall } from '../../context/CallContext';
import '../../styles/personActivity.css';

/**
 * The details pane: who is in here, and the things you can do about it.
 *
 * Membership is rendered from what the server chose to send. A hidden admin is
 * filtered out server-side (utils/conversationAccess.js → visibleMembers), so
 * this component has no notion of hidden members and cannot leak one by
 * accident.
 */

const GroupInfoPanel = ({ conversation, myId, myRole, onClose, onChanged, onLeft, onDeleted, onShowTasks }) => {
    const [addingPeople, setAddingPeople] = useState(false);
    const [editingName, setEditingName] = useState(false);
    const [name, setName] = useState(conversation.name || '');
    const [description, setDescription] = useState(conversation.description || '');
    const descriptionTooLong = description.length > GROUP_DESCRIPTION_LIMIT;
    const [invite, setInvite] = useState(null);
    const [busy, setBusy] = useState(false);
    // Set between choosing a file and uploading it, so the picture that
    // reaches S3 is the one the person actually framed.
    const [pendingIcon, setPendingIcon] = useState(null);

    const isGroup = conversation.kind === 'group';
    const members = conversation.members || [];
    const navigate = useNavigate();
    const { startCall, isBusy } = useCall() || {};

    // Admin and HR, and a Manager or Team Lead for their own team, can open a
    // person's profile (and Activity) from their name here. Everyone else
    // sees plain names, as before.
    const people = conversation.kind === 'group'
        ? members.map((m) => m.user?._id).filter((uid) => uid && String(uid) !== String(myId))
        : [conversation.otherUser?._id];
    const canOpen = useActivityAccess(people, myRole);
    const profileName = (uid, label) => (uid && canOpen.has(String(uid)) ? (
        <button
            type="button"
            className="msgr-profile-link"
            title={`Open ${label}'s profile`}
            onClick={() => navigate(`/employee/${uid}`)}
        >
            {label}
        </button>
    ) : label);
    // A one-to-one chat with a guest is locked to its two people: nobody
    // renames it, adds to it or moderates it (server: isGuestDirect).
    const guestDirect = Boolean(conversation.isGuestDirect);
    const canManage = !guestDirect && (['owner', 'admin'].includes(conversation.myRole)
        || ['ADMIN', 'HR'].includes(myRole));
    // Mirrors canAddMembers / canModerate in server/utils/conversationAccess.js.
    const canAddPeople = !guestDirect && (canManage || conversation.isMember);
    const canModerate = !guestDirect && (canManage
        || (conversation.isMember && ['MANAGER', 'TEAM LEAD'].includes(myRole)));

    const saveDetails = async () => {
        if (!name.trim() || descriptionTooLong) return;
        setBusy(true);
        try {
            const res = await api.put(`/conversations/${conversation._id}`, {
                name: name.trim(),
                description
            });
            onChanged?.(res.data);
            setEditingName(false);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not save.', 'error');
        } finally {
            setBusy(false);
        }
    };

    /**
     * Same crop-then-upload path as a profile photo, and the same `avatar`
     * field name, so ImageEditor and the server route are both reused rather
     * than re-implemented for groups.
     */
    const uploadIcon = async (file) => {
        setPendingIcon(null);
        const data = new FormData();
        data.append('avatar', file);

        Swal.fire({
            title: 'Updating group icon…',
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading()
        });

        try {
            const res = await api.put(`/conversations/${conversation._id}/avatar`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            Swal.close();
            onChanged?.(res.data);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not update the icon.', 'error');
        }
    };

    const removeIcon = async () => {
        const data = new FormData();
        data.append('remove', 'true');
        try {
            const res = await api.put(`/conversations/${conversation._id}/avatar`, data, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            onChanged?.(res.data);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not remove the icon.', 'error');
        }
    };

    const addMembers = async (ids) => {
        if (!ids.length) return;
        try {
            const res = await api.post(`/conversations/${conversation._id}/members`, {
                memberIds: ids
            });
            onChanged?.(res.data);
            setAddingPeople(false);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not add them.', 'error');
        }
    };

    const removeMember = async (user) => {
        const ok = await Swal.fire({
            title: `Remove ${user.name}?`,
            text: 'They will lose access to this group.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Remove'
        });
        if (!ok.isConfirmed) return;

        try {
            await api.delete(`/conversations/${conversation._id}/members/${user._id}`);
            onChanged?.({ ...conversation, refetch: true });
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not remove them.', 'error');
        }
    };

    const leave = async () => {
        const ok = await Swal.fire({
            title: 'Leave this group?',
            text: 'You will stop receiving its messages.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Leave'
        });
        if (!ok.isConfirmed) return;

        try {
            await api.delete(`/conversations/${conversation._id}/members/${myId}`);
            onLeft?.(conversation._id);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not leave.', 'error');
        }
    };

    /**
     * Delete the group outright (admin only).
     *
     * Typing the name is not ceremony. Everything else in this panel is
     * recoverable - a removed member can be added back, a renamed group
     * renamed again - and this is the one button that destroys a conversation
     * and every file in it for everybody, with no undo. A dialog you can
     * dismiss with the space bar is the wrong shape for that.
     */
    const deleteGroup = async () => {
        const isProject = conversation.groupType === 'project';

        const ok = await Swal.fire({
            title: `Delete "${conversation.name}"?`,
            html: `
                <p style="text-align:left;font-size:14px;line-height:1.6;color:light-dark(#334155, var(--theme-ink-soft, #cfd3d9));">
                    This removes the conversation for
                    <b>all ${members.length} member${members.length === 1 ? '' : 's'}</b>,
                    along with every message, photo, file and recording in it.
                    Any guests lose access and their links stop working.
                </p>
                <p style="text-align:left;font-size:13.5px;color:light-dark(#b91c1c, var(--theme-danger-ink, #f09a9d));">
                    <b>This cannot be undone.</b> The content is gone; only a record that
                    you deleted it is kept.
                </p>
                ${isProject ? `
                <p style="text-align:left;font-size:13px;color:light-dark(#b45309, var(--theme-warning-ink, #f0c27a));background:light-dark(#fffbeb, var(--theme-warning-soft, #433520));padding:9px 11px;border-radius:8px;">
                    This is a project group. A new empty one will be created
                    automatically the next time somebody is assigned a task on this
                    project.
                </p>` : ''}
                <p style="text-align:left;font-size:13px;color:light-dark(#64748b, var(--theme-muted, #959ba5));">
                    Type <b>${conversation.name}</b> to confirm.
                </p>`,
            icon: 'warning',
            input: 'text',
            inputPlaceholder: conversation.name,
            showCancelButton: true,
            confirmButtonColor: '#dc2626',
            confirmButtonText: 'Delete this group',
            preConfirm: (typed) => {
                if ((typed || '').trim() !== conversation.name) {
                    Swal.showValidationMessage('That does not match the group name.');
                    return false;
                }
                return true;
            }
        });
        if (!ok.isConfirmed) return;

        try {
            const res = await api.delete(`/conversations/${conversation._id}`);
            Swal.fire({
                icon: 'success',
                title: 'Group deleted',
                text: `"${res.data.name}" and its ${res.data.messages} message`
                    + `${res.data.messages === 1 ? '' : 's'} have been removed.`,
                timer: 2200,
                showConfirmButton: false
            });
            onDeleted?.(conversation._id);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not delete the group.', 'error');
        }
    };

    const toggleMute = async () => {
        try {
            const res = await api.put(`/conversations/${conversation._id}/mute`, {
                muted: !conversation.muted
            });
            onChanged?.({ ...conversation, muted: res.data.muted });
        } catch (err) {
            Swal.fire('Error', 'Could not update notifications.', 'error');
        }
    };

    const makeInvite = async () => {
        try {
            const res = await api.post(`/conversations/${conversation._id}/invite`, { days: 7 });
            setInvite(res.data);
        } catch (err) {
            Swal.fire('Error', err.response?.data?.message || 'Could not create a link.', 'error');
        }
    };

    const inviteUrl = invite?.token
        ? `${window.location.origin}/chats/join/${invite.token}`
        : '';

    // An outside app's notifications: nothing to manage but whether it rings.
    if (conversation.kind === 'app') {
        return (
            <div className="msgr-info">
                <div className="msgr-info-head">
                    <button
                        className="msgr-icon-btn"
                        style={{ position: 'absolute', right: 12, top: 12 }}
                        onClick={onClose}
                    >
                        <FontAwesomeIcon icon={faXmark} />
                    </button>
                    <GroupIcon conversation={conversation} className="msgr-avatar" />
                    <h4>{conversation.title}</h4>
                    <p>Notifications from a connected app. Only you can see this chat.</p>
                </div>

                <div className="msgr-info-section">
                    <button className="msgr-btn block" onClick={toggleMute}>
                        <FontAwesomeIcon icon={conversation.muted ? faBellSlash : faBell} />
                        {conversation.muted ? 'Unmute notifications' : 'Mute notifications'}
                    </button>
                    <p style={{ fontSize: 12, color: 'light-dark(#667781, var(--theme-muted))', marginTop: 8, marginBottom: 0 }}>
                        Muted, new notifications still arrive here but no longer
                        ring or appear in the bell.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="msgr-info">
            <div className="msgr-info-head">
                <button
                    className="msgr-icon-btn"
                    style={{ position: 'absolute', right: 12, top: 12 }}
                    onClick={onClose}
                >
                    <FontAwesomeIcon icon={faXmark} />
                </button>

                {isGroup ? (
                    <div className="msgr-icon-edit">
                        <GroupIcon conversation={conversation} className="msgr-avatar" />
                        {canManage && (
                            <>
                                <label
                                    className="msgr-icon-camera"
                                    title="Change group icon"
                                    htmlFor="msgr-group-icon"
                                >
                                    <FontAwesomeIcon icon={faCamera} />
                                </label>
                                <input
                                    id="msgr-group-icon"
                                    type="file"
                                    accept="image/*"
                                    hidden
                                    onChange={(e) => {
                                        const f = e.target.files?.[0];
                                        if (f) setPendingIcon(f);
                                        e.target.value = '';
                                    }}
                                />
                            </>
                        )}
                    </div>
                ) : (
                    <WithPresence userId={conversation.otherUser?._id}>
                        <Avatar
                            name={conversation.title}
                            profilePic={conversation.avatarUrl}
                            className="msgr-avatar"
                        />
                    </WithPresence>
                )}

                {editingName ? (
                    <>
                        <div className="msgr-field">
                            <input value={name} onChange={(e) => setName(e.target.value)} />
                        </div>
                        <div className="msgr-field">
                            <RichTextEditor
                                rows={2}
                                placeholder="Description"
                                value={description}
                                onChange={setDescription}
                                maxLength={GROUP_DESCRIPTION_LIMIT}
                                enforceMaxLength={false}
                            />
                            {descriptionTooLong && <p className="msgr-description-error" role="alert">Description exceeds {GROUP_DESCRIPTION_LIMIT.toLocaleString()} characters, including formatting. Shorten it to save.</p>}
                        </div>
                        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                            <button className="msgr-btn" onClick={() => setEditingName(false)}>Cancel</button>
                            <button className="msgr-btn primary" onClick={saveDetails} disabled={busy || descriptionTooLong}>
                                Save
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <h4>
                            {isGroup ? conversation.title : profileName(conversation.otherUser?._id, conversation.title)}
                            {isGroup && canManage && (
                                <button
                                    className="msgr-icon-btn"
                                    style={{ width: 26, height: 26, marginLeft: 6 }}
                                    onClick={() => setEditingName(true)}
                                    title="Edit"
                                >
                                    <FontAwesomeIcon icon={faPen} style={{ fontSize: 11 }} />
                                </button>
                            )}
                        </h4>
                        <p>
                            {isGroup
                                ? `${members.length} member${members.length === 1 ? '' : 's'}`
                                : (orgLabel(conversation.otherUser?.jobTitle) || conversation.otherUser?.role || '')}
                        </p>
                        {conversation.description && (
                            <RichText text={conversation.description} style={{ marginTop: 8 }} />
                        )}
                        {!isGroup && <p><LastSeen userId={conversation.otherUser?._id} /></p>}
                        {/* The things you came to a person's card to do. Same
                            conditions as the chat header's own buttons. */}
                        {!isGroup && conversation.isMember && !conversation.myHidden && (
                            <div className="msgr-info-actions">
                                <button type="button" onClick={onClose}>
                                    <FontAwesomeIcon icon={faComment} /> Chat
                                </button>
                                <button
                                    type="button"
                                    onClick={() => startCall?.(conversation, 'audio')}
                                    disabled={isBusy}
                                    title={isBusy ? 'You are already in a call' : `Call ${conversation.title}`}
                                >
                                    <FontAwesomeIcon icon={faPhone} /> Call
                                </button>
                                {onShowTasks && (
                                    <button type="button" onClick={onShowTasks}>
                                        <FontAwesomeIcon icon={faListCheck} /> Tasks
                                    </button>
                                )}
                            </div>
                        )}
                        {isGroup && canManage && conversation.avatar && (
                            <button
                                className="msgr-btn"
                                style={{ marginTop: 10 }}
                                onClick={removeIcon}
                            >
                                <FontAwesomeIcon icon={faTrash} /> Remove icon
                            </button>
                        )}
                    </>
                )}
            </div>

            {conversation.groupType === 'project' && conversation.projectId && (
                <div className="msgr-info-section">
                    <h5>Project</h5>
                    <div style={{ fontSize: 13.5 }}>
                        {conversation.projectId.name}
                        {conversation.projectId.status && (
                            <span className="msgr-tag" style={{ marginLeft: 8 }}>
                                {conversation.projectId.status}
                            </span>
                        )}
                    </div>
                    <p style={{ fontSize: 12, color: 'light-dark(#667781, var(--theme-muted))', marginTop: 8, marginBottom: 0 }}>
                        People are added here automatically when they are assigned a task
                        on this project.
                    </p>
                </div>
            )}

            {/* Keyed on the conversation so switching chats with the panel open
                starts fresh instead of briefly showing the last chat's files. */}
            <SharedContent
                key={conversation._id}
                conversationId={conversation._id}
            />

            <div className="msgr-info-section">
                <button className="msgr-btn block" onClick={toggleMute}>
                    <FontAwesomeIcon icon={conversation.muted ? faBellSlash : faBell} />
                    {conversation.muted ? 'Unmute notifications' : 'Mute notifications'}
                </button>
            </div>

            {isGroup && (
                <div className="msgr-info-section">
                    <h5>{members.length} Members</h5>

                    {canAddPeople && (
                        <button
                            className="msgr-btn block"
                            style={{ marginBottom: 10 }}
                            onClick={() => setAddingPeople(true)}
                        >
                            <FontAwesomeIcon icon={faUserPlus} /> Add people
                        </button>
                    )}

                    {members.map((m) => {
                        const u = m.user || {};
                        return (
                            <div className="msgr-member" key={u._id || m.user}>
                                <WithPresence userId={u._id}>
                                    <Avatar name={u.name} profilePic={u.profilePic} className="msgr-avatar" />
                                </WithPresence>
                                <div className="msgr-member-body">
                                    <div className="msgr-member-name">
                                        {profileName(u._id, u.name)}{String(u._id) === String(myId) ? ' (You)' : ''}
                                    </div>
                                    <div className="msgr-member-role">{orgLabel(u.jobTitle) || u.role}</div>
                                </div>
                                {m.role !== 'member' && <span className="msgr-pill">{m.role}</span>}
                                {canModerate && String(u._id) !== String(myId) && m.role !== 'owner' && (
                                    <button
                                        className="msgr-icon-btn"
                                        onClick={() => removeMember(u)}
                                        title={`Remove ${u.name}`}
                                    >
                                        <FontAwesomeIcon icon={faTrash} style={{ fontSize: 12 }} />
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Feature draft Module 2. Placed directly under the member list,
                because "who is in this room" is one question and the answer has
                two halves - colleagues above, outsiders here. */}
            {isGroup && (
                <ExternalParticipants conversation={conversation} canManage={canManage} canInvite={canAddPeople} />
            )}

            {isGroup && canModerate && (
                <div className="msgr-info-section">
                    <h5>Invite link</h5>
                    {inviteUrl ? (
                        <>
                            <div style={{ fontSize: 12, wordBreak: 'break-all', marginBottom: 8 }}>
                                {inviteUrl}
                            </div>
                            <button
                                className="msgr-btn block"
                                onClick={() => {
                                    navigator.clipboard?.writeText(inviteUrl);
                                    Swal.fire({
                                        icon: 'success', title: 'Link copied',
                                        timer: 1200, showConfirmButton: false
                                    });
                                }}
                            >
                                <FontAwesomeIcon icon={faCopy} /> Copy link
                            </button>
                            <p style={{ fontSize: 11.5, color: 'light-dark(#667781, var(--theme-muted))', marginTop: 8, marginBottom: 0 }}>
                                Expires in 7 days. For colleagues only — anyone logged into
                                GTS Portal with this link can join. To bring in someone from
                                outside the company, use Invite someone from outside above.
                            </p>
                        </>
                    ) : (
                        <button className="msgr-btn block" onClick={makeInvite}>
                            <FontAwesomeIcon icon={faLink} /> Create an invite link
                        </button>
                    )}
                </div>
            )}

            {isGroup && !guestDirect && conversation.isMember && (
                <div className="msgr-info-section">
                    <button className="msgr-btn danger block" onClick={leave}>
                        <FontAwesomeIcon icon={faRightFromBracket} /> Leave group
                    </button>
                </div>
            )}

            {/*
              * Admin only, and last on the panel.
              *
              * Not offered to a group owner: every other destructive action
              * here affects one person or one membership, while this one ends
              * a conversation the whole company may be relying on as a record.
              * That is a decision for somebody who answers for the whole
              * system, not for whoever happened to create the group.
              */}
            {isGroup && myRole === 'ADMIN' && (
                <div className="msgr-info-section msgr-danger-zone">
                    <h5>Danger zone</h5>
                    <p className="ext-hint" style={{ marginTop: 0, marginBottom: 10 }}>
                        Deleting removes this conversation and everything in it for every
                        member. It cannot be undone.
                    </p>
                    <button className="msgr-btn danger block" onClick={deleteGroup}>
                        <FontAwesomeIcon icon={faTrash} /> Delete this group
                    </button>
                </div>
            )}

            {pendingIcon && (
                <ImageEditor
                    file={pendingIcon}
                    onCancel={() => setPendingIcon(null)}
                    onConfirm={uploadIcon}
                />
            )}

            {addingPeople && (
                <PeoplePicker
                    title="Add people"
                    excludeIds={members.map((m) => String(m.user?._id || m.user))}
                    onCancel={() => setAddingPeople(false)}
                    onConfirm={addMembers}
                />
            )}
        </div>
    );
};

export default GroupInfoPanel;
